/**
 * QUIPWA — study preferences, replicated across devices
 *
 * They used to live only in this browser's localStorage, so a phone and a
 * laptop could schedule the same cards with different limits. Now they are a
 * key in the replicated `_state.db` (`settings` table, last-writer-wins), with
 * the store below as the in-memory view the UI and the scheduler read.
 *
 * ```ts
 * const study = useUserSettings((s) => s.study)
 * useUserSettings.getState().updateStudy({ desired_retention: 0.92 })
 * ```
 */
import { create } from 'zustand'
import { DEFAULT_USER_SETTINGS, type UserSettings } from '../../types/domain.ts'

export const STUDY_SETTINGS_KEY = 'study'
const LEGACY_LOCAL_KEY = 'user_settings'

/** Allowed ranges. Inputs clamp to these; the scheduler can trust them. */
export const STUDY_LIMITS = {
  new_cards_per_day: { min: 0, max: 200 },
  max_reviews_per_day: { min: 10, max: 2000 },
  target_session_minutes: { min: 5, max: 120 },
  desired_retention: { min: 0.8, max: 0.97 },
} as const

function clamp(value: number, min: number, max: number): number {
  return Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : min
}

/** Fill defaults and force every field into its range. */
export function normalizeStudySettings(input: Partial<UserSettings> | null | undefined): UserSettings {
  const s = { ...DEFAULT_USER_SETTINGS, ...(input ?? {}) }
  return {
    ...s,
    new_cards_per_day: Math.round(clamp(s.new_cards_per_day, STUDY_LIMITS.new_cards_per_day.min, STUDY_LIMITS.new_cards_per_day.max)),
    max_reviews_per_day: Math.round(clamp(s.max_reviews_per_day, STUDY_LIMITS.max_reviews_per_day.min, STUDY_LIMITS.max_reviews_per_day.max)),
    target_session_minutes: Math.round(clamp(s.target_session_minutes, STUDY_LIMITS.target_session_minutes.min, STUDY_LIMITS.target_session_minutes.max)),
    desired_retention: Math.round(clamp(s.desired_retention, STUDY_LIMITS.desired_retention.min, STUDY_LIMITS.desired_retention.max) * 100) / 100,
  }
}

export type RetentionBand = 'light' | 'balanced' | 'intense'

/**
 * How demanding a target retention is. Review load grows steeply above ~0.92
 * (each extra point costs more reviews than the one before), and below ~0.85
 * forgetting becomes noticeable — hence the three bands Settings describes.
 */
export function retentionBand(retention: number): RetentionBand {
  if (retention < 0.85) return 'light'
  if (retention > 0.92) return 'intense'
  return 'balanced'
}

export interface SettingsBackend {
  getSetting: <T>(key: string) => Promise<T | null>
  setSetting: <T>(key: string, value: T) => Promise<void>
}

interface UserSettingsState {
  study: UserSettings
  hydrated: boolean
  /** Load from the replicated DB (migrating the old localStorage copy once). */
  hydrate: (backend: SettingsBackend) => Promise<void>
  updateStudy: (patch: Partial<UserSettings>) => Promise<void>
  resetStudy: () => Promise<void>
}

let backendRef: SettingsBackend | null = null

function readLegacy(): Partial<UserSettings> | null {
  try {
    const raw = localStorage.getItem(LEGACY_LOCAL_KEY)
    return raw ? (JSON.parse(raw) as Partial<UserSettings>) : null
  } catch {
    return null
  }
}

export const useUserSettings = create<UserSettingsState>((set, get) => ({
  // Until the DB is open, fall back to the legacy local copy so the scheduler
  // never runs with defaults the user had changed.
  study: normalizeStudySettings(readLegacy()),
  hydrated: false,

  hydrate: async (backend) => {
    backendRef = backend
    let stored = await backend.getSetting<Partial<UserSettings>>(STUDY_SETTINGS_KEY)
    if (!stored) {
      const legacy = readLegacy()
      if (legacy) {
        stored = normalizeStudySettings(legacy)
        await backend.setSetting(STUDY_SETTINGS_KEY, stored)
      }
    }
    set({ study: normalizeStudySettings(stored), hydrated: true })
  },

  updateStudy: async (patch) => {
    const next = normalizeStudySettings({ ...get().study, ...patch })
    set({ study: next })
    await backendRef?.setSetting(STUDY_SETTINGS_KEY, next)
  },

  resetStudy: async () => {
    set({ study: { ...DEFAULT_USER_SETTINGS } })
    await backendRef?.setSetting(STUDY_SETTINGS_KEY, DEFAULT_USER_SETTINGS)
  },
}))
